import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, switchMap, take } from 'rxjs';
import { ClientsApiService, ClientUsersApiService } from '../../core/api/services';
import { AuthService } from '../../main';
import { ClientService } from '../';
import { AlertService } from '../../shared/services/alert.service';
import { ArkUserType, ClientModel, ClientPermissionViewModel, ClientRoleViewModel, ClientUserViewModel } from '../../core/api/models';
import { Utils } from '../../core/utils';

@Component({
  selector: 'app-client-user',
  templateUrl: './client-user.component.html',
  standalone: false
})
export class ClientUserComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  currentItem: ClientModel | null = null;
  client_id: number | null = null;
  clientUserID: number | null = null;
  roles: ClientRoleViewModel[] = [];
  permissions: ClientPermissionViewModel[] = [];
  clientUserItem?: ClientUserViewModel;
  loading = false;
  loadingUser = false;

  get canEditAssignments(): boolean {
    return this.authService.hasUserType(ArkUserType.Admin);
  }

  constructor(
    private readonly authService: AuthService,
    private readonly clientService: ClientService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly apiService: ClientsApiService,
    private readonly clientUsersApi: ClientUsersApiService
  ) {}

  ngOnInit(): void {
    this.authService.checkIfAutorized([ArkUserType.Admin, ArkUserType.Support]);



    this.clientService.getCurrentClient().subscribe((client) => {

      if (client){
         this.currentItem = client;
          this.client_id = client.ID||0;;

          this.load();
    }
    });


    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const userID = Number(params.get('UserID'));
      this.clientUserID = userID;
      this.clientUserItem = undefined;
      this.roles = [];
      this.permissions = [];
      this.load();
    });
  }
  
  load(): void {
    if (this.loadingUser || !(this.client_id && this.client_id > 0) || !(this.clientUserID && this.clientUserID > 0)) {
      this.loadingUser = false;
      return;
    }
    this.loadingUser =true;
    this.authService.user().pipe(take(1), switchMap(() => forkJoin({
      //client: this.apiService.Get({ body: { id: clientID } }),
      user: this.clientUsersApi.Get({ clientID: this.client_id||0, body: { id: this.clientUserID||0 } }),
      roles: this.canEditAssignments
        ? this.clientUsersApi.SearchRoles({ clientID: this.client_id||0, body: { count: 1000 } })
        : of([] as ClientRoleViewModel[]),
      permissions: this.canEditAssignments
        ? this.clientUsersApi.GetClientPermissions({ clientID: this.client_id||0 })
        : of([] as ClientPermissionViewModel[])
    })), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        
        this.loadingUser = false;
        //this.currentItem = data.client;
        if (!data.user?.ID) {
          this.alertService.error('Client user not found.');
          return;
        }
        this.clientUserItem = {
          ...data.user,
          PermissionIDs: [...(data.user.PermissionIDs ?? [])],
          UserRoleIDs: [...(data.user.UserRoleIDs ?? [])]
        };
        this.roles = data.roles;
        for (const roleID of this.clientUserItem.UserRoleIDs ?? []) {
          if (!this.roles.some(role => role.RoleID === roleID))
            this.roles.push({ RoleID: roleID, Role: `Role #${roleID}` });
        }
        this.permissions = data.permissions;
        for (const permissionID of this.clientUserItem.PermissionIDs ?? []) {
          if (!this.permissions.some(permission => permission.PermissionID === permissionID))
            this.permissions.push({ PermissionID: permissionID, Permission: `Permission #${permissionID}` });
        }
      },
      error: error => {
        
        this.loadingUser = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }




  save(): void {
    const clientID = this.client_id;
    const user = this.clientUserItem;
    if (this.loading || this.loadingUser || !clientID || !user?.ID || !this.canEditAssignments) return;
    const body: ClientUserViewModel = {
      ...user,
      PermissionIDs: [...(user.PermissionIDs ?? [])],
      UserRoleIDs: [...(user.UserRoleIDs ?? [])]
    };
    this.loading = true;
    this.clientUsersApi.Save({ clientID, body }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loading = false;
        if (this.client_id !== clientID || this.clientUserID !== user.ID) return;
        this.alertService.success('Client user saved successfully');
        this.router.navigate([`/client/${clientID}/Users`]);
      },
      error: error => {
        this.loading = false;
        if (this.client_id === clientID && this.clientUserID === user.ID)
          this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  editOnOpto(): void {
    const clientID = this.client_id;
    const userID = this.clientUserItem?.ID;
    const siteName = this.currentItem?.SiteName;
    if (this.loading || !clientID || !userID) return;
    this.loading = true;
    var url = this.clientService.getClientURL(this.currentItem);
        
    window.open(url + '/Administration/ManageUsers/Edit/' + userID, siteName || '_blank');
      
  }

  resetPassword(): void {
    const clientID = this.client_id;
    const userID = this.clientUserItem?.ID;
    if (this.loading || !clientID || !userID) return;
    this.alertService.confirm('Reset user password', 'Send this user a password reset email?')
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
        if (!response || this.loading || this.client_id !== clientID || this.clientUserID !== userID) return;
        this.loading = true;
        this.clientUsersApi.ResetAccessCredentials({ clientID, id: userID })
          .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
              this.loading = false;
              if (this.client_id === clientID && this.clientUserID === userID)
                this.alertService.success('Password reset email sent!');
            },
            error: error => {
              this.loading = false;
              if (this.client_id === clientID && this.clientUserID === userID)
                this.alertService.error(Utils.getAjaxError(error));
            }
          });
      });
  }
}
